import styled, { css } from 'styled-components'

const reveal = css`
  animation: overviewReveal 0.48s ease both;
  @keyframes overviewReveal { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`

export const OverviewRoot = styled.div`
  width: 100%;
  overflow: hidden;
  border-radius: 22px;
  ${reveal}
`;

export const Hero = styled.section`
  position: relative;
  min-height: 410px;
  padding: clamp(58px, 8vw, 104px) clamp(22px, 5vw, 74px);
  overflow: hidden;
  background:
    radial-gradient(circle at 18% 18%, ${({ theme }) => theme.palette.colors.shellGlow}, transparent 36%),
    linear-gradient(135deg, ${({ theme }) => theme.palette.colors.projectHeroStart}, ${({ theme }) => theme.palette.colors.projectHeroEnd});
  color: ${({ theme }) => theme.palette.primary.contrastText};
  text-align: center;
  &::after {
    content: '';
    position: absolute;
    width: 360px;
    height: 360px;
    right: -150px;
    bottom: -210px;
    border: 1px solid ${({ theme }) => theme.palette.colors.projectHeroBorder};
    border-radius: 50%;
  }
`;
export const HeroLogo = styled.img`
  width: 190px;
  max-width: 54vw;
  height: auto;
`;
export const HeroTitle = styled.h1`
  margin: 14px 0 0;
  font-family: Gilroy, sans-serif;
  font-size: clamp(42px, 6vw, 76px);
  letter-spacing: -0.055em;
  line-height: 0.98;
`;
export const HeroSubtitle = styled.h2`
  margin: 20px 0 0;
  font-family: Gilroy, sans-serif;
  font-size: clamp(19px, 2.2vw, 27px);
  font-weight: 600;
`;
export const HeroDescription = styled.p`
  max-width: 720px;
  margin: 14px auto 0;
  color: ${({ theme }) => theme.palette.colors.projectHeroMuted};
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.55;
`;
export const Section = styled.section`
  padding: clamp(54px, 7vw, 94px) clamp(22px, 5vw, 74px);
  background: ${({ alternate, theme }) => alternate ? theme.palette.colors.controlSurface : theme.palette.colors.background};
`;
export const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.palette.colors.text};
  font-family: Gilroy, sans-serif;
  font-size: clamp(28px, 3.4vw, 42px);
  letter-spacing: -0.035em;
  text-align: center;
`;
export const SectionDescription = styled.p`
  max-width: 720px;
  margin: 10px auto 38px;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.55;
  text-align: center;
`;

export const Comparison = styled.div`
  max-width: 1000px;
  margin: 36px auto 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 22px;
  align-items: center;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
export const ComparisonCard = styled.div`
  min-height: 250px;
  padding: 28px;
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-top: 3px solid ${({ before, theme }) => before ? theme.palette.colors.subdued : theme.palette.success.main};
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.colors.cardSurface};
  box-shadow: ${({ theme }) => theme.palette.colors.cardShadow};
`;
export const ComparisonLabel = styled.span`
  display: inline-flex;
  padding: 5px 12px;
  border-radius: 999px;
  background: ${({ before, theme }) => before ? theme.palette.colors.chipSurface : theme.palette.colors.successMuted};
  color: ${({ before, theme }) => before ? theme.palette.colors.subdued : theme.palette.success.main};
  font-family: Poppins, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
export const ComparisonList = styled.ul`
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    padding: 8px 0 8px 28px;
    color: ${({ theme }) => theme.palette.colors.text};
    font-family: Lato, sans-serif;
    font-size: 15px;
    line-height: 1.45;
  }
  li::before {
    content: '${({ before }) => before ? '×' : '✓'}';
    position: absolute;
    left: 0;
    color: ${({ before, theme }) => before ? theme.palette.colors.subdued : theme.palette.success.main};
    font-weight: 800;
  }
`;
export const Arrow = styled.span`
  width: ${({ pipeline }) => pipeline ? '34px' : '44px'};
  height: ${({ pipeline }) => pipeline ? '34px' : '44px'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.palette.blue.main};
  border-radius: 50%;
  color: ${({ theme }) => theme.palette.blue.main};
  font-size: 22px;
  @media (max-width: 760px) { transform: rotate(90deg); margin: 0 auto; }
`;

export const Pipeline = styled.div`
  max-width: 1180px;
  margin: 40px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 980px) { flex-direction: column; gap: 10px; }
`;
export const PipelineStep = styled.div`
  flex: 1;
  max-width: 190px;
  min-height: 230px;
  padding: 0 18px 18px;
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.colors.cardSurface};
  box-shadow: ${({ theme }) => theme.palette.colors.cardShadow};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  @media (max-width: 980px) { max-width: 340px; width: 100%; }
`;
export const PipelineStepNumber = styled.span`
  width: 32px;
  height: 32px;
  margin: -16px 0 18px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const Icon = styled.svg`
  width: 32px;
  height: 32px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
  color: ${({ theme }) => theme.palette.blue.main};
`;
export const PipelineStepTitle = styled.strong`
  margin-top: 12px;
  color: ${({ theme }) => theme.palette.colors.text};
  font-family: Gilroy, sans-serif;
  font-size: 17px;
`;
export const PipelineStepDescription = styled.span`
  flex: 1;
  margin: 8px 0 16px;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 13px;
  line-height: 1.45;
`;
export const ToolBadge = styled.span`
  display: inline-flex;
  margin: 3px 4px 0 0;
  padding: 4px 9px;
  border-radius: 999px;
  background: ${({ tool, theme }) => tool?.includes('Tavily') ? theme.palette.colors.successMuted : tool === 'Playwright' ? theme.palette.colors.warningMuted : theme.palette.colors.badgeMuted};
  color: ${({ tool, theme }) => tool?.includes('Tavily') ? theme.palette.success.main : tool === 'Playwright' ? theme.palette.warning.main : theme.palette.blue.main};
  font-family: Poppins, sans-serif;
  font-size: 10px;
  font-weight: 800;
`;
export const FeedbackLoop = styled.div`
  max-width: 380px;
  margin: 18px auto 0;
  color: ${({ theme }) => theme.palette.warning.main};
  font-family: Lato, sans-serif;
  font-size: 12px;
  text-align: center;
`;
export const PipelineNote = styled.p`
  max-width: 960px;
  margin: 26px auto 0;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
`;

export const ModuleGrid = styled.div`
  max-width: 1120px;
  margin: 40px auto 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
  @media (max-width: 880px) { grid-template-columns: 1fr; }
`;
export const ModuleCard = styled.article`
  min-height: 360px;
  padding: 28px;
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.colors.cardSurface};
  box-shadow: ${({ theme }) => theme.palette.colors.cardShadow};
  display: flex;
  flex-direction: column;
  transition: transform 0.18s ease, border-color 0.18s ease;
  &:hover { transform: translateY(-3px); border-color: ${({ theme }) => theme.palette.colors.borderStrong}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
export const ModuleIcon = styled.div`
  width: 52px;
  height: 52px;
  padding: ${({ logo }) => logo ? '6px' : '10px'};
  border-radius: 13px;
  background: ${({ theme }) => theme.palette.colors.summarySurface};
  color: ${({ type, theme }) => ['news', 'fastapi', 'tavily'].includes(type) ? theme.palette.success.main : ['playwright', 'gitlab'].includes(type) ? theme.palette.warning.main : theme.palette.blue.main};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;
export const SourceLogo = styled.img`
  width: ${({ compact }) => compact ? '19px' : '100%'};
  height: ${({ compact }) => compact ? '19px' : '100%'};
  object-fit: contain;
  border-radius: 4px;
`;
export const ModuleTitle = styled.h3`
  margin: 18px 0 0;
  color: ${({ theme }) => theme.palette.colors.text};
  font-family: Gilroy, sans-serif;
  font-size: 22px;
`;
export const ModuleDescription = styled.p`
  margin: 9px 0 16px;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.55;
`;
export const ModuleList = styled.ul`
  flex: 1;
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    padding: 6px 0 6px 18px;
    color: ${({ theme }) => theme.palette.colors.text};
    font-family: Lato, sans-serif;
    font-size: 13px;
    line-height: 1.45;
  }
  li::before { content: '•'; position: absolute; left: 0; color: ${({ theme }) => theme.palette.blue.main}; font-weight: 800; }
`;

export const DataPanel = styled.div`
  max-width: 1120px;
  margin: 28px auto 0;
  padding: clamp(24px, 4vw, 44px);
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-radius: 16px;
  background: ${({ theme }) => theme.palette.colors.summarySurface};
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.65;
  h3 { margin: 26px 0 8px; color: ${({ theme }) => theme.palette.blue.main}; font-family: Poppins, sans-serif; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; }
  h3:first-child { margin-top: 0; }
  p { margin: 0; }
  strong { color: ${({ theme }) => theme.palette.colors.text}; }
  small { display: block; margin-top: 8px; color: ${({ theme }) => theme.palette.colors.subdued}; line-height: 1.5; }
`;
export const DataColumns = styled.div`
  margin: 24px 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;
export const Examples = styled.div`display: flex; flex-direction: column; gap: 9px;`;
export const Example = styled.div`
  padding: 13px 16px;
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-radius: 10px;
  background: ${({ theme }) => theme.palette.colors.cardSurface};
  display: grid;
  grid-template-columns: 116px minmax(0, 1fr);
  gap: 12px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
export const ExampleSource = styled.span`
  color: ${({ theme }) => theme.palette.blue.main};
  font-family: Poppins, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;
export const ExampleCode = styled.code`
  color: ${({ theme }) => theme.palette.colors.text};
  font-size: 12px;
  line-height: 1.55;
  word-break: break-word;
`;

export const TechnologyGrid = styled.div`
  max-width: 960px;
  margin: 38px auto 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;
export const TechnologyItem = styled.div`
  min-height: 190px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.colors.cardSurface};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
export const TechnologyName = styled.strong`
  margin-top: 14px;
  color: ${({ theme }) => theme.palette.colors.text};
  font-family: Gilroy, sans-serif;
  font-size: 17px;
`;
export const TechnologyRole = styled.span`
  margin-top: 7px;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 13px;
  line-height: 1.45;
`;

export const Operations = styled.div`
  max-width: 760px;
  margin: 38px auto 0;
  text-align: center;
`;
export const OperationsValue = styled.strong`
  color: ${({ theme }) => theme.palette.success.main};
  font-family: Gilroy, sans-serif;
  font-size: clamp(36px, 6vw, 62px);
`;
export const OperationsDescription = styled.p`
  max-width: 640px;
  margin: 8px auto 28px;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 15px;
  line-height: 1.55;
`;
export const OperationsRows = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
`;
export const CostRow = styled.div`
  padding: 15px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 18px;
  text-align: left;
  @media (max-width: 540px) { grid-template-columns: 1fr; gap: 4px; }
`;
export const CostLabel = styled.strong`
  color: ${({ theme }) => theme.palette.colors.text};
  font-family: Lato, sans-serif;
  font-size: 14px;
`;
export const CostDetail = styled.span`
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.45;
`;

export const StatusTimeline = styled.div`
  position: relative;
  max-width: 760px;
  margin: 38px auto 0;
  padding-left: 36px;
  &::before { content: ''; position: absolute; left: 10px; top: 0; bottom: 0; width: 2px; background: ${({ theme }) => theme.palette.colors.cardBorder}; }
`;
export const Phase = styled.article`
  position: relative;
  margin-bottom: 22px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.palette.colors.cardBorder};
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.colors.cardSurface};
  &::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    left: -36px;
    top: 20px;
    border: 3px solid ${({ status, theme }) => status === 'done' ? theme.palette.success.main : status === 'next' ? theme.palette.warning.main : theme.palette.blue.main};
    border-radius: 50%;
    background: ${({ status, theme }) => status === 'done' ? theme.palette.success.main : theme.palette.colors.controlSurface};
  }
`;
export const PhaseBadge = styled.span`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ status, theme }) => status === 'done' ? theme.palette.colors.successMuted : status === 'next' ? theme.palette.colors.warningMuted : theme.palette.colors.badgeMuted};
  color: ${({ status, theme }) => status === 'done' ? theme.palette.success.main : status === 'next' ? theme.palette.warning.main : theme.palette.blue.main};
  font-family: Poppins, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
export const PhaseTitle = styled.h3`
  margin: 10px 0 0;
  color: ${({ theme }) => theme.palette.colors.text};
  font-family: Gilroy, sans-serif;
  font-size: 20px;
`;
export const PhaseDescription = styled.p`
  margin: 7px 0 0;
  color: ${({ theme }) => theme.palette.colors.muted};
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.55;
`;
